import { dashboardApi } from "@api";
import {
  IChartCoupon,
  IClientInfo,
  IClinicCoupon,
  IClinicHcp,
  ICouponVisualData,
} from "@interfaces";
import { useQuery } from "@tanstack/react-query";

export const dashboardQueryKeys = {
  all: ["dashboard"] as const,
  couponCount: (type: string) => [...dashboardQueryKeys.all, "couponCount", type] as const,
  couponInfo: (region: string) => [...dashboardQueryKeys.all, "couponInfo", region] as const,
  clientAgeInfo: () => [...dashboardQueryKeys.all, "clientAgeInfo"] as const,
  clinicGraph: (region: string) => [...dashboardQueryKeys.all, "clinicGraph", region] as const,
  clinicHcpCount: () => [...dashboardQueryKeys.all, "clinicHcpCount"] as const,
};

const couponCountInit: ICouponVisualData = {
  todayCouponInfo: {},
  patientCount: {},
  couponInfo: {},
};

interface IParams {
  type?: string;
}

interface IReturn {
  data: ICouponVisualData;
  loading: boolean;
}

export const useCouponCount = ({ type = "" }: IParams): IReturn => {
  const { data, isFetching } = useQuery({
    queryKey: dashboardQueryKeys.couponCount(type),
    queryFn: async () => {
      const res = await dashboardApi.couponCount({ type });
      return res.data?.data as ICouponVisualData;
    },
  });

  return {
    data: data ?? couponCountInit,
    loading: isFetching,
  };
};

interface IParamRegion {
  region: string;
}

export const useCouponInfo = ({
  region,
}: IParamRegion): { data: IChartCoupon[]; loading: boolean } => {
  const { data, isFetching } = useQuery({
    queryKey: dashboardQueryKeys.couponInfo(region),
    queryFn: async () => {
      const res = await dashboardApi.couponInfo({ region });
      return res.data?.data as IChartCoupon[];
    },
    enabled: Boolean(region),
  });

  return {
    data: data ?? [],
    loading: isFetching,
  };
};

export const useClientInfo = (): { data: IClientInfo[]; loading: boolean } => {
  const { data, isFetching } = useQuery({
    queryKey: dashboardQueryKeys.clientAgeInfo(),
    queryFn: async () => {
      const res = await dashboardApi.clientAgeInfo();
      return res.data?.data as IClientInfo[];
    },
  });

  return {
    data: data ?? [],
    loading: isFetching,
  };
};

export const useclinicGraph = ({
  region,
}: {
  region: string;
}): { data: IClinicCoupon[]; loading: boolean } => {
  const { data, isFetching } = useQuery({
    queryKey: dashboardQueryKeys.clinicGraph(region),
    queryFn: async () => {
      const res = await dashboardApi.clinicGraph({ region });
      return res.data?.data as IClinicCoupon[];
    },
    enabled: Boolean(region),
  });

  return {
    data: data ?? [],
    loading: isFetching,
  };
};

export const useclinicHcp = (): { data: IClinicHcp | undefined; loading: boolean } => {
  const { data, isFetching } = useQuery({
    queryKey: dashboardQueryKeys.clinicHcpCount(),
    queryFn: async () => {
      const res = await dashboardApi.clinicHcpCount();
      return res.data?.data as IClinicHcp;
    },
  });

  return {
    data,
    loading: isFetching,
  };
};
